// src/sign_in_files/Step2.jsx
import React, { useState, useEffect, useRef } from 'react';
import { FIELD_LIMITS } from './DesignSystem';
import fundraiserApi from '../services/fundraiserApi';

const Step2 = ({
  onSuccess,
  onError,
  onLoading,
  onSubmitting,
  fundraiserId,
  onBack,
  isLoading,
  isSubmitting,
   userId ,
  apiError
}) => {
  const [formData, setFormData] = useState({
    patientName: '',
    patientAge: '',
    medicalCondition: '',
    hospitalizationStatus: '',
    hospital: '',
    city: '',
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [showMedicalConditionDropdown, setShowMedicalConditionDropdown] = useState(false);
  const [filteredConditions, setFilteredConditions] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // File upload states
  const [imageFiles, setImageFiles] = useState([]);
  const [documentFiles, setDocumentFiles] = useState([]);
  const imageInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const [totalFilesCount, setTotalFilesCount] = useState(0);

  // Medical conditions list
  const medicalConditions = [
    'Cancer',
    'Heart Disease',
    'Stroke',
    'Diabetes',
    'Kidney Disease',
    'Liver Disease',
    'COVID-19',
    'Pneumonia',
    'Tuberculosis',
    'Asthma',
    'Chronic Obstructive Pulmonary Disease (COPD)',
    'Alzheimer\'s Disease',
    'Parkinson\'s Disease',
    'Multiple Sclerosis',
    'Epilepsy',
    'Brain Tumor',
    'Spinal Cord Injury',
    'Arthritis',
    'Osteoporosis',
    'Lupus',
    'HIV/AIDS',
    'Hepatitis',
    'Leukemia',
    'Lymphoma',
    'Breast Cancer',
    'Lung Cancer',
    'Prostate Cancer',
    'Colon Cancer',
    'Pancreatic Cancer',
    'Ovarian Cancer',
    'Brain Cancer',
    'Liver Cancer',
    'Kidney Cancer',
    'Bladder Cancer',
    'Skin Cancer (Melanoma)',
    'Thyroid Cancer',
    'Bone Cancer',
    'Blood Cancer',
    'Cystic Fibrosis',
    'Sickle Cell Anemia',
    'Thalassemia',
    'Hemophilia',
    'Muscular Dystrophy',
    'Cerebral Palsy',
    'Down Syndrome',
    'Autism Spectrum Disorder',
    'Schizophrenia',
    'Bipolar Disorder',
    'Major Depression',
    'Post-Traumatic Stress Disorder (PTSD)',
    'Eating Disorders',
    'Organ Transplant (Heart)',
    'Organ Transplant (Liver)',
    'Organ Transplant (Kidney)',
    'Organ Transplant (Lung)',
    'Organ Transplant (Bone Marrow)',
    'Accident Injuries',
    'Burn Injuries',
    'Spinal Injury',
    'Head Injury',
    'Amputation',
    'Rare Genetic Disorders',
    'Autoimmune Diseases',
    'Rheumatoid Arthritis',
    'Crohn\'s Disease',
    'Ulcerative Colitis',
    'Celiac Disease',
    'Sepsis',
    'Meningitis',
    'Encephalitis',
    'Guillain-Barré Syndrome',
    'Myasthenia Gravis',
    'Fibromyalgia',
    'Chronic Fatigue Syndrome',
    'End Stage Renal Disease (ESRD)',
    'Cirrhosis',
    'Heart Failure',
    'Cardiac Arrest',
    'Coronary Artery Disease',
    'Valvular Heart Disease',
    'Congenital Heart Disease',
    'Premature Birth Complications',
    'Neonatal Intensive Care (NICU)',
    'Pediatric Cancer',
    'Childhood Leukemia',
    'Other'
  ];

  // Update total files count whenever imageFiles or documentFiles change
  useEffect(() => {
    setTotalFilesCount(imageFiles.length + documentFiles.length);
  }, [imageFiles, documentFiles]);

  // Load existing data on mount
  useEffect(() => {
    const loadExistingData = async () => {
      if (!fundraiserId) {
        setIsInitialLoad(false);
        return;
      }

      try {
        // Note: getFundraiserById has been removed from API
        // You can add it back if needed, otherwise just proceed
        console.log('Fundraiser ID:', fundraiserId);
      } catch (error) {
        console.error('Error loading existing data:', error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    loadExistingData();
    setFilteredConditions(medicalConditions);
  }, [fundraiserId]);

  // Filter medical conditions based on input
  const filterMedicalConditions = (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setFilteredConditions([]);
      setShowMedicalConditionDropdown(false);
    } else {
      const filtered = medicalConditions.filter(condition =>
        condition.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredConditions(filtered);
      if (filtered.length > 0) {
        setShowMedicalConditionDropdown(true);
      } else {
        setShowMedicalConditionDropdown(false);
      }
    }
  };

  // File handling functions
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Check if adding these files would exceed the limit
    if (totalFilesCount + files.length > 5) {
      onError(`You can only upload a maximum of 5 files total. You currently have ${totalFilesCount} files.`);
      return;
    }
    
    // Filter for image files only
    const imageFilesOnly = files.filter(file => 
      file.type.startsWith('image/') && 
      ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(file.type.toLowerCase())
    );
    
    if (imageFilesOnly.length === 0) {
      onError('Please select valid image files (JPEG, PNG, GIF, WebP).');
      return;
    }
    
    if (imageFilesOnly.length !== files.length) {
      onError('Some files were not images and were skipped.');
    }
    
    setImageFiles(prev => [...prev, ...imageFilesOnly]);
    
    // Reset the input
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handleDocumentSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Check if adding these files would exceed the limit
    if (totalFilesCount + files.length > 5) {
      onError(`You can only upload a maximum of 5 files total. You currently have ${totalFilesCount} files.`);
      return;
    }
    
    // Filter for document files only
    const documentFilesOnly = files.filter(file => 
      file.type === 'application/pdf' || 
      file.type === 'application/msword' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.name.toLowerCase().endsWith('.doc') ||
      file.name.toLowerCase().endsWith('.docx') ||
      file.name.toLowerCase().endsWith('.pdf')
    );
    
    if (documentFilesOnly.length === 0) {
      onError('Please select valid document files (PDF, DOC, DOCX).');
      return;
    }
    
    if (documentFilesOnly.length !== files.length) {
      onError('Some files were not documents and were skipped.');
    }
    
    setDocumentFiles(prev => [...prev, ...documentFilesOnly]);
    
    // Reset the input
    if (documentInputRef.current) {
      documentInputRef.current.value = '';
    }
  };

  const removeImageFile = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeDocumentFile = (index) => {
    setDocumentFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Function to upload files to API
  // Function to upload files to API


  const validateField = (field, value) => {
    let error = '';
    
    switch (field) {
      case 'patientName':
        if (!value.trim()) error = 'Patient name is required';
        else if (value.length < FIELD_LIMITS.patientName.min) error = `Name must be at least ${FIELD_LIMITS.patientName.min} characters`;
        break;
      case 'medicalCondition':
        if (!value.trim()) error = 'Medical condition is required';
        break;
      case 'hospital':
        if (!value.trim()) error = 'Hospital name is required';
        break;
      case 'patientAge':
      case 'hospitalizationStatus':
      case 'city':
        if (!value || value.toString().trim() === '') error = 'This field is required';
        break;
      default:
        break;
    }
    
    return error;
  };

  const handleBlur = (field) => {
    const error = validateField(field, formData[field]);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
    if (field === 'medicalCondition') {
      setTimeout(() => setShowMedicalConditionDropdown(false), 200);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Handle medical condition search
    if (field === 'medicalCondition') {
      filterMedicalConditions(value);
    }
  };

  const handleSelectMedicalCondition = (condition) => {
    handleInputChange('medicalCondition', condition);
    setShowMedicalConditionDropdown(false);
  };

  const validateForm = () => {
    const newFieldErrors = {};
    const requiredFields = ['patientName', 'patientAge', 'medicalCondition', 'hospitalizationStatus', 'hospital', 'city'];
    
    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newFieldErrors[field] = error;
      }
    });

    setFieldErrors(newFieldErrors);
    return Object.keys(newFieldErrors).length === 0;
  };

  // src/sign_in_files/Step2.jsx

// src/sign_in_files/Step2.jsx

const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateForm()) {
    onError('Please fill all required fields correctly.');
    return;
  }

  try {
    onSubmitting(true);
    onError('');

    // ✅ STEP 1: Save patient details first
    const patientResult = await fundraiserApi.savePatientDetails(fundraiserId, formData, userId);
    
    if (!patientResult?.success) {
      throw new Error(patientResult?.message || 'Failed to save patient details');
    }
    
    console.log('✅ Patient details saved successfully');

    // ✅ STEP 2: Upload files if any
    if (totalFilesCount > 0) {
      console.log(`📤 Uploading ${totalFilesCount} file(s)...`);
      
      const uploadResult = await uploadFiles();
      
      if (!uploadResult.success) {
        // Don't block submission, just warn
        console.warn('⚠️ Some files failed to upload:', uploadResult.error);
        onError(uploadResult.error);
      } else {
        console.log('✅ All files uploaded successfully');
      }
    }
    
    // ✅ STEP 3: Move to next step
    onSuccess(); 
    
  } catch (error) {
    console.error('❌ Error:', error);
    onError(error?.message || 'Failed to save patient details');
  } finally {
    onSubmitting(false);
  }
};

// Function to upload files to API
const uploadFiles = async () => {
  if (totalFilesCount === 0) {
    return { success: true }; // No files to upload
  }

  const allFiles = [...imageFiles, ...documentFiles];
  const uploadResults = [];
  let hasAnySuccess = false;

  for (const file of allFiles) {
    try {
      const formData = new FormData();
      formData.append('fundraiserId', String(fundraiserId));
      formData.append('file', file);

      console.log(`📤 Uploading file: ${file.name}`);
      const response = await fundraiserApi.uploadDocument(formData);
      
      console.log(`📥 Upload response for ${file.name}:`, response);
      
      // Check if upload was successful (handle different response formats)
      if (response && !response.error) {
        uploadResults.push({ file: file.name, success: true });
        hasAnySuccess = true;
        console.log(`✅ File uploaded successfully: ${file.name}`);
      } else {
        const errorMsg = response?.message || response?.error || 'Upload failed';
        uploadResults.push({ 
          file: file.name, 
          success: false, 
          error: errorMsg 
        });
        console.error(`❌ File upload failed for ${file.name}:`, errorMsg);
      }
    } catch (error) {
      console.error(`❌ Error uploading file ${file.name}:`, error);
      
      let errorMessage = 'Upload failed';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      uploadResults.push({ 
        file: file.name, 
        success: false, 
        error: errorMessage 
      });
    }
  }

  // Check if all uploads were successful
  const failedUploads = uploadResults.filter(result => !result.success);
  if (failedUploads.length > 0) {
    return { 
      success: false, 
      error: `Failed to upload ${failedUploads.length} file(s). ${failedUploads[0].error || 'Please try again.'}`,
      failedUploads,
      hasAnySuccess // Return this to show partial success
    };
  }

  return { success: true, results: uploadResults };
};

  const getSubmitButtonText = () => {
    if (isLoading) {
      return 'Saving...';
    }
    return 'Save & Continue →';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Fundraiser ID Display */}
      {fundraiserId && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-xs text-emerald-700 font-medium text-left">
              
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
            Patient Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="patientName"
            value={formData.patientName}
            onChange={(e) => handleInputChange('patientName', e.target.value)}
            onBlur={() => handleBlur('patientName')}
            placeholder="Full name"
            className={`
              w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
              focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
              placeholder:text-gray-400 text-gray-900 text-sm
              ${fieldErrors.patientName 
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                : 'border-gray-200 hover:border-gray-300'
              }
            `}
            maxLength={FIELD_LIMITS.patientName.max}
            required
          />
          {fieldErrors.patientName && (
            <p className="mt-1 text-xs text-red-600 text-left">
              {fieldErrors.patientName}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
            Age <span className="text-red-500">*</span>
          </label>
          <select
            name="patientAge"
            value={formData.patientAge}
            onChange={(e) => handleInputChange('patientAge', e.target.value)}
            className={`
              w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
              focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
              text-gray-900 text-sm bg-white
              ${fieldErrors.patientAge 
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                : 'border-gray-200 hover:border-gray-300'
              }
            `}
            required
          >
            <option value="">Select age</option>
            {Array.from({ length: 100 }, (_, i) => (
              <option key={i + 1} value={(i + 1).toString()}>
                {i + 1}
              </option>
            ))}
          </select>
          {fieldErrors.patientAge && (
            <p className="mt-1 text-xs text-red-600 text-left">
              {fieldErrors.patientAge}
            </p>
          )}
        </div>
      </div>

      <div className="relative">
        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
          Medical Condition <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            name="medicalCondition"
            value={formData.medicalCondition}
            onChange={(e) => handleInputChange('medicalCondition', e.target.value)}
            onFocus={() => {
              if (formData.medicalCondition.trim().length >= 2) {
                filterMedicalConditions(formData.medicalCondition);
              }
            }}
            onBlur={() => setTimeout(() => setShowMedicalConditionDropdown(false), 200)}
            placeholder="Type to search medical condition"
            className={`
              w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
              focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
              placeholder:text-gray-400 text-gray-900 text-sm bg-white
              ${fieldErrors.medicalCondition 
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                : 'border-gray-200 hover:border-gray-300'
              }
            `}
            maxLength={FIELD_LIMITS.medicalCondition.max}
            required
          />
          
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {showMedicalConditionDropdown && filteredConditions.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredConditions.map((condition, index) => (
                <div
                  key={index}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectMedicalCondition(condition);
                  }}
                  className="px-3.5 py-2.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-700 border-b border-gray-100 last:border-b-0 hover:text-gray-900"
                >
                  {condition}
                </div>
              ))}
            </div>
          )}
        </div>
        {fieldErrors.medicalCondition && (
          <p className="mt-1 text-xs text-red-600 text-left">
            {fieldErrors.medicalCondition}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
            Hospitalization Status <span className="text-red-500">*</span>
          </label>
          <select
            name="hospitalizationStatus"
            value={formData.hospitalizationStatus}
            onChange={(e) => handleInputChange('hospitalizationStatus', e.target.value)}
            className={`
              w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
              focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
              text-gray-900 text-sm bg-white
              ${fieldErrors.hospitalizationStatus 
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                : 'border-gray-200 hover:border-gray-300'
              }
            `}
            required
          >
            <option value="">Select status</option>
            <option value="admitted">Currently Admitted</option>
            <option value="discharged">Discharged</option>
            <option value="regular_visits">Regular Visits</option>
            <option value="planned">Planned Admission</option>
          </select>
          {fieldErrors.hospitalizationStatus && (
            <p className="mt-1 text-xs text-red-500 text-left">
              {fieldErrors.hospitalizationStatus}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            onBlur={() => handleBlur('city')}
            placeholder="City"
            className={`
              w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
              focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
              placeholder:text-gray-400 text-gray-900 text-sm
              ${fieldErrors.city 
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                : 'border-gray-200 hover:border-gray-300'
              }
            `}
            maxLength={FIELD_LIMITS.city.max}
            required
          />
          {fieldErrors.city && (
            <p className="mt-1 text-xs text-red-600 text-left">
              {fieldErrors.city}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
          Hospital Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="hospital"
          value={formData.hospital}
          onChange={(e) => handleInputChange('hospital', e.target.value)}
          onBlur={() => handleBlur('hospital')}
          placeholder="Hospital name"
          className={`
            w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
            focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
            placeholder:text-gray-400 text-gray-900 text-sm
            ${fieldErrors.hospital 
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
              : 'border-gray-200 hover:border-gray-300'
            }
          `}
          maxLength={FIELD_LIMITS.hospital.max}
          required
        />
        {fieldErrors.hospital && (
          <p className="mt-1 text-xs text-red-600 text-left">
            {fieldErrors.hospital}
          </p>
        )}
      </div>

      {/* File Upload Section */}
      <div className="mt-6 pt-5 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 text-left">Upload Supporting Files (Optional)</h3>
        <p className="text-sm text-gray-600 mb-4 text-left">
          You can upload up to 5 files total (images and documents). Supported formats: Images (JPEG, PNG, GIF, WebP), Documents (PDF, DOC, DOCX).
        </p>
        
        {/* Images Upload Section */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
            Images (Patient photos, medical images)
          </label>
          <div className="flex items-center space-x-3 mb-3">
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              disabled={totalFilesCount >= 5}
              className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg border border-blue-200 font-medium hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              + Add Images
            </button>
            <input
              type="file"
              ref={imageInputRef}
              onChange={handleImageSelect}
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              multiple
              className="hidden"
              disabled={totalFilesCount >= 5}
            />
            <span className="text-xs text-gray-500">
              {imageFiles.length} image(s) selected
            </span>
          </div>
          
          {/* Image Previews */}
          {imageFiles.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
              {imageFiles.map((file, index) => (
                <div key={index} className="relative group">
                  <div className="bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-24 object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => removeImageFile(index)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-1 truncate">{file.name}</p>
                  <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Documents Upload Section */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
            Documents (Medical reports, prescriptions, etc.)
          </label>
          <div className="flex items-center space-x-3 mb-3">
            <button
              type="button"
              onClick={() => documentInputRef.current?.click()}
              disabled={totalFilesCount >= 5}
              className="px-4 py-2 bg-green-50 text-green-700 rounded-lg border border-green-200 font-medium hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              + Add Documents
            </button>
            <input
              type="file"
              ref={documentInputRef}
              onChange={handleDocumentSelect}
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              multiple
              className="hidden"
              disabled={totalFilesCount >= 5}
            />
            <span className="text-xs text-gray-500">
              {documentFiles.length} document(s) selected
            </span>
          </div>
          
          {/* Document Previews */}
          {documentFiles.length > 0 && (
            <div className="space-y-2 mb-4">
              {documentFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{file.name}</p>
                      <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDocumentFile(index)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* File Count Indicator */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Total files:</span>
            <span className="text-sm font-semibold text-gray-900">
              {totalFilesCount} / 5
            </span>
          </div>
          {totalFilesCount >= 5 && (
            <p className="text-xs text-red-500 mt-1 text-left">
              Maximum file limit reached. Remove some files to add more.
            </p>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className={`flex justify-between mt-6 pt-5 border-t border-gray-100`}>
        <button
          type="button"
          onClick={onBack}
          disabled={isLoading || isSubmitting}
          className="px-6 py-2.5 rounded-lg border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-all disabled:opacity-50"
        >
          ← Back
        </button>
        
        <button
          type="submit"
          disabled={isLoading || isSubmitting}
          className={`
            px-6 py-2.5 rounded-lg transition-all duration-300 font-semibold text-sm
            ${isLoading || isSubmitting
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-gsg-teal to-gsg-teal-dark text-white hover:shadow-md hover:shadow-gsg-teal/25'
            }
            flex items-center justify-center
          `}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {getSubmitButtonText()}
            </>
          ) : (
            getSubmitButtonText()
          )}
        </button>
      </div>

      {/* Footer */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-2">
          <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-blue-700 text-left">
            Your information is secure and will only be used for fundraising purposes.
          </p>
        </div>
      </div>
    </form>
  );
};

export default Step2;